import {
  type DomainCommandContext,
  DomainFault,
  type DomainModule,
  type DomainReadContext,
  type JsonObject,
  type UsageEntry,
} from '../../contracts';
import type { Command } from '../../service';
import type { Entity, Kind, Role, Snapshot } from '../../types';
import { resolveEvidence } from '../recording';
import { publishedExecutionSettings } from './configuration';
import {
  analyzeFrozen,
  fingerprint,
  freezeRecording,
  matrix,
  predictionFor,
  validateCurrentEvidence,
  validateSettings,
} from './engine';
import {
  type AnalysisResultVersion,
  type AnalysisStore,
  type AnalysisTask,
  type AnalysisTemplate,
  type OutputCategory,
  outputLabels,
  type PricingSnapshot,
  type ProcessingSnapshot,
  type QualityReport,
  type QualitySample,
  type QualityTruth,
  type RuleSnapshot,
  type TemplateSettings,
  type TemplateVersion,
} from './types';

export * from './engine';
export * from './types';

function fail(message: string, code = 'VALIDATION'): never {
  throw new DomainFault(code, message);
}
const need = (value: string | undefined, name: string) =>
  value?.trim() || fail(`请填写${name}`);
const encode = (value: unknown) => JSON.stringify(value);
function inputJson<T>(text: string | undefined, name: string): T {
  try {
    if (!text || text.length > 2000000) throw Error();
    return JSON.parse(text);
  } catch {
    return fail(`${name}格式无效`);
  }
}

function parse<T>(row: Entity, field: string): T {
  try {
    if (!row.fields?.[field]) throw Error();
    return JSON.parse(row.fields[field]);
  } catch {
    return fail('领域数据版本不兼容，请保留数据并核实', 'CONFLICT');
  }
}
export const taskOf = (row: Entity): AnalysisTask => parse(row, 'analysisTask');
export const templateOf = (row: Entity): AnalysisTemplate =>
  parse(row, 'analysisTemplate');
export const sampleOf = (row: Entity): QualitySample =>
  parse(row, 'qualitySample');
export const reportOf = (row: Entity): QualityReport =>
  parse(row, 'qualityReport');
const write = (row: Entity, key: string, value: unknown) => {
  row.fields = { ...row.fields, [key]: encode(value) };
};
function entity(
  ctx: DomainReadContext,
  uuid: string | undefined,
  kind?: Kind,
): Entity {
  const row = ctx.get(uuid);
  if (!row || (kind && row.kind !== kind))
    return fail('对象不存在或来源已撤权', 'FORBIDDEN');
  return row;
}
function preview(input: Command, ctx?: DomainReadContext) {
  const at = Number(input.fields?.previewAt);
  if (!Number.isFinite(at) || Math.abs(Date.now() - at) > 900000)
    fail('预览已过期，请重新预览', 'CONFLICT');
  if (ctx && input.fields?.sourceVersions) {
    const versions = inputJson<Record<string, number>>(
      input.fields.sourceVersions,
      '来源版本',
    );
    if (!versions || typeof versions !== 'object' || Array.isArray(versions))
      fail('来源版本格式无效');
    if (
      Object.entries(versions).some(
        ([uuid, revision]) => ctx.get(uuid)?.revision !== revision,
      )
    )
      fail('预览来源版本已变化，请重新预览', 'CONFLICT');
  }
}
export function rulesFor(
  ctx: Pick<DomainReadContext, 'list'>,
  ids: string[],
): RuleSnapshot[] {
  if (!ids.length || new Set(ids).size !== ids.length)
    return fail('请选择不重复的规则');
  return ids.map((uuid) => {
    const row = ctx
      .list('rule')
      .find((r) => r.uuid === uuid && r.status === 'published');
    if (!row?.fields?.keyword?.trim())
      return fail('引用规则未发布、已撤回或缺少关键词');
    return {
      uuid,
      revision: row.revision,
      title: row.title,
      keyword: row.fields.keyword.trim(),
      exclusion: row.fields.exclusion?.trim() || '',
    };
  });
}
export function processingFor(
  ctx: Pick<DomainReadContext, 'list'>,
): ProcessingSnapshot {
  const row = ctx.list('modelConfig').find((m) => m.status === 'published');
  if (!row) return fail('缺少已发布处理配置，请前往处理配置检查');
  const parallel = Number(row.fields?.parallel),
    timeoutMs = Number(row.fields?.timeoutMs);
  if (
    !Number.isInteger(parallel) ||
    parallel < 1 ||
    parallel > 3 ||
    !Number.isFinite(timeoutMs) ||
    timeoutMs < 1 ||
    timeoutMs > 3600000
  )
    return fail('已发布处理配置的并发数或超时无效');
  return {
    uuid: row.uuid,
    revision: row.revision,
    title: row.title,
    parallel,
    timeoutMs,
  };
}
function pricingFor(ctx: DomainReadContext): PricingSnapshot {
  const row = ctx.list('subscription').find((r) => r.status === 'published');
  if (
    !row ||
    !Number.isFinite(Date.parse(row.fields?.expiresAt || '')) ||
    Date.parse(row.fields?.expiresAt || '') <= Date.parse(ctx.now)
  )
    return fail('模拟服务配置缺失或已过期', 'QUOTA_EXCEEDED');
  const price = Number(row.fields?.unitPrice);
  if (!Number.isFinite(price) || price < 0 || price > 100000)
    return fail('模拟计价配置无效');
  return {
    uuid: row.uuid,
    revision: row.revision,
    publishedAt: row.updatedAt,
    centsPerMinute: price,
    expiresAt: row.fields?.expiresAt || '',
  };
}
export function templateSamplePreview(ctx: DomainReadContext, row: Entity) {
  const data = templateOf(row),
    test = data.test;
  if (!test?.rules || !test.processing) return undefined;
  const source = entity(ctx, test.recordingUuid, 'recording');
  if (
    source.revision !== test.recordingRevision ||
    source.versions?.at(-1)?.uuid !== test.transcriptVersionUuid
  )
    return undefined;
  const input = freezeRecording(source);
  const draft: TemplateVersion = {
    uuid: 'sandbox',
    version: data.versions.length + 1,
    createdAt: test.testedAt,
    note: '草稿测试',
    settings: data.draft,
    rules: test.rules,
    processing: test.processing,
  };
  const current = data.versions.find(
    (v) => v.uuid === data.publishedVersionUuid,
  );
  return {
    draft: analyzeFrozen(input, draft, test.rules, test.testedAt),
    published: current
      ? analyzeFrozen(input, current, current.rules, test.testedAt)
      : undefined,
  };
}
export function publishedTemplate(row: Entity) {
  const data = templateOf(row);
  const version = data.versions.find(
    (v) => v.uuid === data.publishedVersionUuid,
  );
  if (row.status !== 'published' || !version) return fail('模板未发布或已停用');
  return version;
}
export function prepareSubmission(
  ctx: DomainReadContext,
  recordingUuids: string[],
  templateUuid: string,
  parentTaskUuid?: string,
) {
  if (
    !recordingUuids.length ||
    recordingUuids.length > 50 ||
    new Set(recordingUuids).size !== recordingUuids.length
  )
    return fail('请选择1至50条不重复录音');
  const templateEntity = entity(ctx, templateUuid, 'template');
  const template = publishedTemplate(templateEntity);
  const rules = rulesFor(ctx, template.settings.ruleUuids);
  const processing = processingFor(ctx);
  const pricing = pricingFor(ctx);
  const inputs = recordingUuids.map((uuid) =>
    freezeRecording(entity(ctx, uuid, 'recording')),
  );
  const quota = ctx.list('subscription').find((r) => r.uuid === pricing.uuid)
    ?.fields?.analysisLimit;
  const count = ctx
    .list('job')
    .filter((j) => !['failed', 'cancelled'].includes(j.status)).length;
  if (Number.isFinite(Number(quota)) && count + 1 > Number(quota))
    return fail('当前范围模拟分析配额不足', 'QUOTA_EXCEEDED');
  return {
    inputs,
    template,
    templateUuid,
    templateRevision: templateEntity.revision,
    rules,
    processing,
    pricing,
    parentTaskUuid,
    fingerprint: fingerprint({
      inputs: inputs.map((i) => [i.uuid, i.revision, i.transcriptVersionUuid]),
      templateUuid,
      templateRevision: templateEntity.revision,
      rules,
      processing,
      pricing,
    }),
  };
}
export function taskResults(
  ctx: DomainReadContext,
  row: Entity,
): AnalysisResultVersion[] {
  const task = taskOf(row);
  for (const i of task.items) entity(ctx, i.input.uuid, 'recording');
  return task.items.flatMap((i) => (i.result ? [i.result] : []));
}
export function findAnalysisResult(
  ctx: DomainReadContext,
  uuid: string | undefined,
): { job: Entity; result?: AnalysisResultVersion } | undefined {
  if (!uuid) return undefined;
  const direct = ctx.get(uuid);
  if (direct?.kind === 'job')
    return {
      job: direct,
      result: direct.fields?.analysisTask
        ? taskResults(ctx, direct)[0]
        : undefined,
    };
  for (const job of ctx.list('job').filter((j) => j.fields?.analysisTask)) {
    const result = taskResults(ctx, job).find((r) => r.uuid === uuid);
    if (result) return { job, result };
  }
  return undefined;
}
export function usageFor(ctx: DomainReadContext): UsageEntry[] {
  const store = ctx.data() as unknown as Partial<AnalysisStore>;
  return (store.usage || []).filter((u) => Boolean(ctx.get(u.sourceUuid)));
}
function recordUsage(
  ctx: DomainCommandContext,
  row: Entity,
  task: AnalysisTask,
  item: AnalysisTask['items'][number],
  success: boolean,
) {
  const store = structuredClone(
    ctx.data(),
  ) as unknown as Partial<AnalysisStore>;
  store.usage ||= [];
  const businessKey = `${row.uuid}:${item.uuid}:${success ? 'success' : `failed:${item.attempts}`}`;
  if (!store.usage.some((u) => u.businessKey === businessKey))
    store.usage.push({
      uuid: crypto.randomUUID(),
      businessKey,
      sourceUuid: row.uuid,
      occurredAt: ctx.now,
      quantity: success ? item.input.durationMs : 0,
      unit: 'millisecond',
      status: success ? 'succeeded' : 'failed',
      pricing: {
        uuid: task.pricing.uuid,
        revision: task.pricing.revision,
        publishedAt: task.pricing.publishedAt,
      },
      amountInCents: success
        ? Math.ceil(
            (item.input.durationMs * task.pricing.centsPerMinute) / 60000,
          )
        : 0,
    });
  ctx.setData(store as unknown as JsonObject);
}
function submit(ctx: DomainCommandContext, input: Command) {
  preview(input, ctx);
  const parent = input.uuid ? entity(ctx, input.uuid, 'job') : undefined;
  const ids = inputJson<string[]>(input.fields?.recordingUuids, '录音输入');
  if (!Array.isArray(ids)) return fail('录音输入须为列表');
  const prepared = prepareSubmission(
    ctx,
    ids,
    need(input.fields?.templateUuid, '模板'),
    parent?.uuid,
  );
  if (input.fields?.inputFingerprint !== prepared.fingerprint)
    return fail('录音、模板或发布配置已变化，请重新预览', 'CONFLICT');
  const first = prepared.inputs[0];
  const task: AnalysisTask = {
    schema: 1,
    templateUuid: prepared.templateUuid,
    template: prepared.template,
    rules: prepared.rules,
    processing: prepared.processing,
    pricing: prepared.pricing,
    generation: 1,
    parentTaskUuid: parent?.uuid,
    createdBy: ctx.identity.memberUuid,
    items: prepared.inputs.map((source, index) => ({
      uuid: crypto.randomUUID(),
      input: source,
      state: 'queued',
      attempts: 1,
      resultVersion:
        ctx
          .list('job')
          .filter((j) =>
            j.fields?.analysisTask
              ? taskOf(j).items.some((i) => i.input.uuid === source.uuid)
              : j.sourceUuid === source.uuid,
          ).length + 1,
      checkpoint: 0,
      elapsedMs: 0,
      simulateFailure:
        input.fields?.simulateFailure === 'true' &&
        index === prepared.inputs.length - 1,
    })),
  };
  return ctx.append(
    'job',
    input.title?.trim() || `模拟分析 · ${task.items.length} 条录音`,
    {
      status: 'queued',
      progress: 0,
      campus: first.campus,
      memberUuid: first.memberUuid,
      sourceUuid: first.uuid,
      generation: 1,
      fields: {
        analysisTask: encode(task),
        sourceRecordingUuids: encode(prepared.inputs.map((i) => i.uuid)),
        templateUuid: prepared.templateUuid,
      },
    },
  );
}
function control(ctx: DomainCommandContext, input: Command) {
  const row = entity(ctx, input.uuid, 'job');
  const task = taskOf(row);
  preview(input, ctx);
  if (input.fields?.operation === 'cancel') {
    if (!['queued', 'running'].includes(row.status))
      return fail('任务不在可取消阶段', 'INVALID_TRANSITION');
    task.generation++;
    for (const item of task.items)
      if (['queued', 'running'].includes(item.state)) item.state = 'cancelled';
    row.status = 'cancelled';
  } else if (input.fields?.operation === 'retry') {
    if (!['failed', 'partial'].includes(row.status))
      return fail('仅失败或部分成功任务可重试', 'INVALID_TRANSITION');
    for (const item of task.items)
      if (item.state === 'failed') {
        entity(ctx, item.input.uuid, 'recording');
        item.state = 'queued';
        item.checkpoint = 0;
        item.elapsedMs = 0;
        item.failure = undefined;
        item.attempts++;
      }
    task.generation++;
    row.status = 'queued';
    row.note = '';
  } else return fail('任务操作无效');
  write(row, 'analysisTask', task);
  row.generation = task.generation;
  ctx.touch(row);
  return row;
}
export function advanceAnalysis(
  ctx: DomainCommandContext,
  milliseconds: number,
) {
  let active = 0;
  for (const row of ctx
    .list('job')
    .filter(
      (j) => j.fields?.analysisTask && ['queued', 'running'].includes(j.status),
    )
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))) {
    const task = taskOf(row);
    let touched = false;
    for (const item of task.items) {
      if (item.state === 'queued' && active < task.processing.parallel) {
        item.state = 'running';
        touched = true;
      }
      if (item.state !== 'running') continue;
      active++;
      if (active > task.processing.parallel) continue;
      touched = true;
      item.elapsedMs += milliseconds;
      const source = ctx.get(item.input.uuid);
      if (
        !source ||
        !source.versions?.some(
          (v) => v.uuid === item.input.transcriptVersionUuid,
        )
      ) {
        item.state = 'failed';
        item.failure = '冻结来源已撤权或原转写版本不可用';
      } else if (item.elapsedMs > task.processing.timeoutMs) {
        item.state = 'failed';
        item.failure = '超过冻结处理配置的超时限制；可重分析使用新配置';
      } else if (
        item.simulateFailure &&
        item.attempts === 1 &&
        item.checkpoint >= 25
      ) {
        item.state = 'failed';
        item.failure = '模拟处理连接中断；仅重试此失败对象';
      } else {
        item.checkpoint = Math.min(100, item.checkpoint + 25);
        if (item.checkpoint === 100) {
          item.result = analyzeFrozen(
            item.input,
            task.template,
            task.rules,
            ctx.now,
            item.resultVersion,
          );
          item.state = 'succeeded';
        }
      }
      if (item.state === 'failed' || item.state === 'succeeded') {
        recordUsage(ctx, row, task, item, item.state === 'succeeded');
        active--;
      }
    }
    if (!touched) continue;
    const completed = task.items.filter((i) => i.state === 'succeeded').length,
      failures = task.items.filter((i) => i.state === 'failed').length;
    row.status =
      completed + failures === task.items.length
        ? failures
          ? completed
            ? 'partial'
            : 'failed'
          : 'succeeded'
        : 'running';
    row.progress = Math.round(
      task.items.reduce(
        (n, i) => n + (i.state === 'failed' ? 100 : i.checkpoint),
        0,
      ) / task.items.length,
    );
    row.note = failures ? `${failures} 条失败；已成功对象保持结果和原用量` : '';
    write(row, 'analysisTask', task);
    ctx.touch(row);
    if (['partial', 'failed', 'succeeded'].includes(row.status))
      ctx.notify(
        row,
        `分析${row.status === 'succeeded' ? '完成' : '需要处理'}：${row.title}`,
      );
  }
}
function confirmCandidate(
  ctx: DomainCommandContext,
  input: Command,
  kind: 'clue' | 'plan' | 'followUp' | 'knowledge',
  category: OutputCategory,
) {
  preview(input, ctx);
  const row = entity(ctx, input.uuid, 'job');
  const results = taskResults(ctx, row);
  const candidate = results
    .flatMap((r) => r.suggestions)
    .find(
      (c) => c.uuid === input.fields?.candidateUuid && c.category === category,
    );
  if (!candidate) return fail('候选不存在或类别不匹配');
  const evidence = resolveEvidence(ctx, candidate.evidence);
  const store = structuredClone(ctx.data()) as unknown as AnalysisStore;
  const reference = store.confirmed?.[candidate.uuid];
  if (reference && !ctx.get(reference))
    return fail('该建议已生成对象，当前视角无权重复创建', 'CONFLICT');
  const existing = reference
    ? ctx.get(reference)
    : ctx
        .list(kind)
        .find((e) => e.fields?.analysisCandidateUuid === candidate.uuid);
  if (existing) return existing;
  const recording = entity(ctx, evidence.recordingUuid, 'recording');
  const student =
    kind === 'followUp'
      ? entity(ctx, input.fields?.studentUuid, 'student')
      : undefined;
  if (student && student.campus !== recording.campus)
    return fail('服务对象必须属于来源校区');
  const created = ctx.append(kind, need(input.title, '候选标题'), {
    status:
      kind === 'clue'
        ? 'pendingReview'
        : kind === 'followUp'
          ? 'candidate'
          : 'draft',
    sourceUuid: row.uuid,
    campus: recording.campus,
    memberUuid: recording.memberUuid,
    evidence,
    note: need(input.note, '人工确认依据'),
    owner: ctx.context.viewMemberUuid,
    fields: {
      analysisCandidateUuid: candidate.uuid,
      analysisTaskUuid: row.uuid,
      recordingUuid: recording.uuid,
      transcriptVersionUuid: evidence.transcriptVersionUuid,
      observationStatus: kind === 'plan' ? 'candidate' : '',
      authorView: ctx.context.viewMemberUuid,
      studentUuid: student?.uuid || '',
    },
  });
  store.confirmed ||= {};
  store.confirmed[candidate.uuid] = created.uuid;
  ctx.setData(store as unknown as JsonObject);
  return created;
}
function saveTemplate(ctx: DomainCommandContext, input: Command) {
  const settings = inputJson<TemplateSettings>(
    input.fields?.settings,
    '模板设置',
  );
  validateSettings(settings);
  rulesFor(ctx, settings.ruleUuids);
  const source = input.uuid ? entity(ctx, input.uuid, 'template') : undefined;
  const copy = input.fields?.copy === 'true';
  const row =
    source && !copy
      ? source
      : ctx.append('template', need(input.title, '模板名称'), {
          status: 'draft',
          campus:
            ctx.context.campus === '全部校区'
              ? ctx.members()[0]?.campus || '第一校区'
              : ctx.context.campus,
        });
  const data: AnalysisTemplate =
    source && !copy ? templateOf(source) : { draft: settings, versions: [] };
  data.draft = settings;
  delete data.test;
  row.title = need(input.title, '模板名称');
  write(row, 'analysisTemplate', data);
  ctx.touch(row);
  return row;
}
function templateCommand(ctx: DomainCommandContext, input: Command) {
  preview(input, ctx);
  const row = entity(ctx, input.uuid, 'template');
  const data = templateOf(row);
  const op = input.fields?.operation;
  if (op === 'test') {
    validateSettings(data.draft);
    const recording = entity(ctx, input.fields?.recordingUuid, 'recording');
    const frozen = freezeRecording(recording);
    const rules = rulesFor(ctx, data.draft.ruleUuids);
    const processing = processingFor(ctx);
    const v: TemplateVersion = {
      uuid: 'sandbox',
      version: data.versions.length + 1,
      createdAt: ctx.now,
      note: '模拟样本测试',
      settings: data.draft,
      rules,
      processing,
    };
    const result = analyzeFrozen(frozen, v, rules, ctx.now);
    data.test = {
      rules: structuredClone(rules),
      processing: structuredClone(processing),
      fingerprint: fingerprint({ draft: data.draft, rules, processing }),
      testedAt: ctx.now,
      recordingUuid: recording.uuid,
      recordingRevision: recording.revision,
      transcriptVersionUuid: frozen.transcriptVersionUuid,
      candidateCount: result.suggestions.length,
      contributions: result.contributions,
    };
  } else if (op === 'publish') {
    const rules = rulesFor(ctx, data.draft.ruleUuids),
      processing = processingFor(ctx);
    if (
      !data.test ||
      data.test.fingerprint !==
        fingerprint({ draft: data.draft, rules, processing })
    )
      return fail('草稿或已发布配置已变化，请重新测试后发布');
    const testedSource = entity(ctx, data.test.recordingUuid, 'recording');
    if (
      testedSource.revision !== data.test.recordingRevision ||
      testedSource.versions?.at(-1)?.uuid !== data.test.transcriptVersionUuid
    )
      return fail('测试样本已有新版本，请重新测试');
    const v: TemplateVersion = {
      uuid: crypto.randomUUID(),
      version: data.versions.length + 1,
      createdAt: ctx.now,
      note: need(input.note, '发布说明'),
      settings: structuredClone(data.draft),
      rules,
      processing,
    };
    data.versions.push(v);
    data.publishedVersionUuid = v.uuid;
    row.status = 'published';
  } else if (op === 'rollback') {
    const previous = data.versions.find(
      (v) => v.uuid === input.fields?.versionUuid,
    );
    if (!previous) return fail('回退版本不存在');
    const v = {
      ...structuredClone(previous),
      uuid: crypto.randomUUID(),
      version: data.versions.length + 1,
      createdAt: ctx.now,
      note: `回退至版本${previous.version}：${need(input.note, '回退原因')}`,
    };
    data.versions.push(v);
    data.publishedVersionUuid = v.uuid;
    data.draft = structuredClone(v.settings);
    delete data.test;
    row.status = 'published';
  } else if (op === 'disable') {
    if (row.status !== 'published') return fail('仅已发布模板可停用');
    row.status = 'disabled';
  } else return fail('模板操作无效');
  write(row, 'analysisTemplate', data);
  ctx.touch(row);
  return row;
}
function saveSample(ctx: DomainCommandContext, input: Command) {
  preview(input, ctx);
  const source = entity(ctx, input.fields?.recordingUuid, 'recording');
  const old = input.uuid ? entity(ctx, input.uuid, 'evaluation') : undefined;
  const frozen = old ? sampleOf(old).input : freezeRecording(source);
  const start = Number(input.fields?.startMs),
    end = Number(input.fields?.endMs);
  const evidence = validateCurrentEvidence(source, frozen, start, end);
  const truth = (input.fields?.truth || 'unlabelled') as QualityTruth;
  if (!['positive', 'negative', 'unknown', 'unlabelled'].includes(truth))
    return fail('人工标签无效');
  const row =
    old ||
    ctx.append('evaluation', need(input.title, '样本名称'), {
      status: 'draft',
      sourceUuid: source.uuid,
      campus: source.campus,
      memberUuid: source.memberUuid,
      evidence,
    });
  const previous = old ? sampleOf(old) : undefined;
  if (
    previous &&
    (previous.evidence.recordingUuid !== evidence.recordingUuid ||
      previous.evidence.transcriptVersionUuid !==
        evidence.transcriptVersionUuid ||
      previous.evidence.startMs !== evidence.startMs ||
      previous.evidence.endMs !== evidence.endMs)
  )
    return fail('冻结样本不允许替换来源，请新建样本');
  const note = need(input.note, '标注依据');
  const sample: QualitySample = {
    schema: 1,
    input: previous?.input || frozen,
    evidence: previous?.evidence || evidence,
    truth,
    labelNote: note,
    labelHistory: [
      ...(previous?.labelHistory || []),
      { at: ctx.now, truth, note, actor: ctx.identity.memberUuid },
    ],
    subject: input.fields?.subject || frozen.scene,
    noise: input.fields?.noise || '未标注',
    participants: input.fields?.participants || '未标注',
  };
  write(row, 'qualitySample', sample);
  row.evidence = sample.evidence;
  row.status = truth === 'unlabelled' ? 'draft' : 'confirmed';
  ctx.touch(row);
  return row;
}
function feedback(ctx: DomainCommandContext, input: Command) {
  preview(input, ctx);
  const task = entity(ctx, input.uuid, 'job');
  const candidate = taskResults(ctx, task)
    .flatMap((r) => r.suggestions)
    .find((c) => c.uuid === input.fields?.candidateUuid);
  if (!candidate) return fail('候选不存在');
  resolveEvidence(ctx, candidate.evidence);
  const truth = input.fields?.truth as QualityTruth;
  if (!['positive', 'negative', 'unknown'].includes(truth))
    return fail('请选择正确、误报或信息不足');
  const store = structuredClone(ctx.data()) as unknown as AnalysisStore;
  const reference = store.feedback?.[candidate.uuid];
  if (reference && !ctx.get(reference))
    return fail('该建议已有反馈，请在质量评估中更新', 'CONFLICT');
  const existing = reference
    ? ctx.get(reference)
    : ctx
        .list('evaluation')
        .find((e) => e.fields?.feedbackCandidateUuid === candidate.uuid);
  const source = entity(ctx, candidate.evidence.recordingUuid, 'recording');
  const row =
    existing ||
    ctx.append('evaluation', `结果反馈 · ${candidate.title}`, {
      status: 'confirmed',
      campus: source.campus,
      memberUuid: source.memberUuid,
      evidence: candidate.evidence,
      sourceUuid: source.uuid,
      fields: { feedbackCandidateUuid: candidate.uuid },
    });
  const result = taskResults(ctx, task).find((r) =>
    r.suggestions.some((c) => c.uuid === candidate.uuid),
  );
  if (!result) return fail('结果不存在');
  const previous = existing ? sampleOf(existing) : undefined;
  const note = need(input.note, '反馈依据');
  write(row, 'qualitySample', {
    schema: 1,
    input: result.input,
    evidence: candidate.evidence,
    truth,
    labelNote: note,
    labelHistory: [
      ...(previous?.labelHistory || []),
      { at: ctx.now, truth, note, actor: ctx.identity.memberUuid },
    ],
    subject: result.input.scene,
    noise: '未标注',
    participants: '未标注',
  } satisfies QualitySample);
  ctx.touch(row);
  store.feedback ||= {};
  store.feedback[candidate.uuid] = row.uuid;
  ctx.setData(store as unknown as JsonObject);
  return row;
}
function evaluate(ctx: DomainCommandContext, input: Command) {
  preview(input, ctx);
  const ids = inputJson<string[]>(input.fields?.sampleUuids, '样本集合');
  if (!Array.isArray(ids) || ids.length > 500)
    return fail('样本须为不超过500项的列表');
  if (!ids.length || new Set(ids).size !== ids.length)
    return fail('请选择不重复的样本');
  const rows = ids.map((id) => entity(ctx, id, 'evaluation'));
  const expected = input.fields?.sampleRevisions
    ? inputJson<Record<string, number>>(
        input.fields.sampleRevisions,
        '样本版本',
      )
    : undefined;
  if (expected && rows.some((row) => expected[row.uuid] !== row.revision))
    return fail('人工标注版本已变化，请重新预览', 'CONFLICT');
  const samples = rows.map((row) => {
    const s = sampleOf(row);
    resolveEvidence(ctx, s.evidence);
    return s;
  });
  const refs = inputJson<{ templateUuid: string; versionUuid: string }[]>(
    input.fields?.versions,
    '比较版本',
  );
  if (
    !Array.isArray(refs) ||
    !refs.length ||
    refs.length > 2 ||
    new Set(refs.map((r) => r.versionUuid)).size !== refs.length
  )
    return fail('请选择1至2个模板版本');
  const missesReviewed = input.fields?.missesReviewed === 'true';
  const note = need(input.note, '评估范围及漏报覆盖说明');
  const report: QualityReport = {
    uuid: crypto.randomUUID(),
    createdAt: ctx.now,
    sampleRevisions: Object.fromEntries(rows.map((r) => [r.uuid, r.revision])),
    samples: rows.map((r, i) => ({
      uuid: r.uuid,
      truth: samples[i].truth,
      subject: samples[i].subject,
      noise: samples[i].noise,
    })),
    missesReviewed,
    coverageNote: note,
    compared: refs.map((ref) => {
      const template = entity(ctx, ref.templateUuid, 'template');
      const v = templateOf(template).versions.find(
        (v) => v.uuid === ref.versionUuid,
      );
      if (!v) return fail('比较版本不存在');
      const predictions = samples.map((s, i) => ({
        sampleUuid: rows[i].uuid,
        truth: s.truth,
        predicted: predictionFor(s, v),
      }));
      return {
        ...ref,
        name: `${template.title} · v${v.version}`,
        predictions,
        matrix: matrix(predictions, missesReviewed),
      };
    }),
  };
  return ctx.append('evaluation', input.title?.trim() || '模拟样本评估快照', {
    uuid: report.uuid,
    status: 'confirmed',
    campus: rows[0].campus,
    memberUuid: rows[0].memberUuid,
    evidence: rows[0].evidence,
    fields: {
      qualityReport: encode(report),
      sourceRecordingUuids: encode(samples.map((s) => s.input.uuid)),
    },
  });
}
function migrate(state: Snapshot) {
  state.domainData ||= {};
  state.domainData.analysis ||= { usage: [] };
  const configurations = (kind: Kind) =>
    publishedExecutionSettings(
      {
        ...state,
        context: {
          ...state.context,
          role: 'groupOperator',
          campus: '全部校区',
        },
      },
      kind,
    );
  for (const row of state.entities.filter(
    (e) => e.kind === 'template' && !e.fields?.analysisTemplate,
  )) {
    const outputs = Object.entries(outputLabels)
      .filter(([, label]) => label === row.fields?.output)
      .map(([key]) => key as OutputCategory);
    const settings: TemplateSettings = {
      scene: row.fields?.scene || '全部场景',
      outputs: outputs.length ? outputs : ['review'],
      ruleUuids: configurations('rule')
        .filter((r) => r.fields?.keyword)
        .map((r) => r.uuid),
      minMatches: 1,
      prompt: '',
    };
    const data: AnalysisTemplate = { draft: settings, versions: [] };
    try {
      validateSettings(settings);
      const ctx = { list: configurations };
      const rules = rulesFor(ctx, settings.ruleUuids),
        processing = processingFor(ctx);
      if (row.status === 'published' && row.fields?.tested === 'true') {
        const version: TemplateVersion = {
          uuid: fingerprint([row.uuid, row.revision, 'legacy-template']),
          version: 1,
          createdAt: row.createdAt,
          note: '保留现有已测试模拟种子模板',
          settings,
          rules,
          processing,
        };
        data.versions.push(version);
        data.publishedVersionUuid = version.uuid;
      } else if (row.status === 'published') row.status = 'draft';
    } catch {
      if (row.status === 'published') row.status = 'draft';
    }
    write(row, 'analysisTemplate', data);
  }
}
const analysts: Role[] = [
  'teacher',
  'campusManager',
  'regionManager',
  'groupOperator',
  'researchLead',
  'reviewer',
  'studentService',
];
const editors: Role[] = [
  'campusManager',
  'regionManager',
  'groupOperator',
  'researchLead',
  'systemAdmin',
];
const evaluators: Role[] = [
  'campusManager',
  'regionManager',
  'groupOperator',
  'researchLead',
  'reviewer',
];
export const analysisDomain: DomainModule = {
  namespace: 'analysis',
  version: 1,
  resource: 'analysis',
  pages: [
    {
      id: 'A01',
      path: '/analysis/job',
      resource: 'analysis',
      parentPath: '/analysis/job',
      returnTo: '/analysis/job',
    },
    {
      id: 'A02',
      path: '/analysis/result/:resultUuid',
      resource: 'analysis',
      parentPath: '/analysis/job',
      returnTo: '/analysis/job',
    },
  ],
  migrations: [{ version: 1, up: migrate }],
  commands: {
    submit: { roles: analysts, kind: 'job', execute: submit },
    control: { roles: analysts, kind: 'job', execute: control },
    confirmClue: {
      roles: ['teacher', 'campusManager', 'groupOperator', 'reviewer'],
      kind: 'clue',
      execute: (ctx, input) => confirmCandidate(ctx, input, 'clue', 'review'),
    },
    confirmObservation: {
      roles: ['teacher', 'campusManager', 'groupOperator', 'researchLead'],
      kind: 'plan',
      execute: (ctx, input) => confirmCandidate(ctx, input, 'plan', 'teaching'),
    },
    confirmService: {
      roles: ['campusManager', 'groupOperator', 'studentService'],
      kind: 'followUp',
      execute: (ctx, input) =>
        confirmCandidate(ctx, input, 'followUp', 'service'),
    },
    confirmKnowledge: {
      roles: ['teacher', 'campusManager', 'groupOperator', 'researchLead'],
      kind: 'knowledge',
      execute: (ctx, input) =>
        confirmCandidate(ctx, input, 'knowledge', 'knowledge'),
    },
    feedback: { roles: analysts, kind: 'evaluation', execute: feedback },
  },
  tick: advanceAnalysis,
  suggestions: (ctx) =>
    ctx
      .list('job')
      .filter(
        (j) =>
          j.fields?.analysisTask && ['failed', 'partial'].includes(j.status),
      )
      .map((j) => ({
        id: `retry:${j.uuid}`,
        label: '重试分析失败对象',
        sourceUuid: j.uuid,
        action: 'analysis.control',
        fields: { operation: 'retry' },
      })),
  metrics: (ctx) => {
    const jobs = ctx.list('job').filter((j) => j.fields?.analysisTask);
    const usage = usageFor(ctx);
    return [
      {
        uuid: 'analysis-success-v1',
        scope: ctx.context.campus,
        asOf: ctx.now,
        metric: '分析成功对象',
        numerator: jobs
          .flatMap((j) => taskOf(j).items)
          .filter((i) => i.state === 'succeeded').length,
        denominator: jobs.flatMap((j) => taskOf(j).items).length || null,
        unit: 'item',
        sourceUuids: jobs.map((j) => j.uuid),
        definitionVersion: 'A-C1-v1',
      },
      {
        uuid: 'analysis-cost-v1',
        scope: ctx.context.campus,
        asOf: ctx.now,
        metric: '成功模拟费用',
        numerator: usage.reduce((n, u) => n + u.amountInCents, 0),
        denominator: null,
        unit: 'cent',
        sourceUuids: jobs.map((j) => j.uuid),
        definitionVersion: 'A-C1-v1',
      },
    ];
  },

  query: (ctx) => ({ usage: usageFor(ctx) }),
  search: (ctx) =>
    ctx
      .list('job')
      .filter((r) => r.fields?.analysisTask)
      .map((r) => ({
        id: r.uuid,
        entityUuid: r.uuid,
        title: r.title,
        text: taskResults(ctx, r)
          .flatMap((result) => result.suggestions.map((s) => s.observation))
          .join(' '),
        kind: r.kind,
        memberUuid: r.memberUuid,
        occurredAt: r.createdAt,
      })),
};
export const analysisTemplateDomain: DomainModule = {
  namespace: 'analysis-template',
  version: 1,
  resource: 'analysisTemplate',
  pages: [
    {
      id: 'A03',
      path: '/analysis/template',
      resource: 'analysisTemplate',
      parentPath: '/analysis/job',
      returnTo: '/analysis/template',
    },
  ],
  migrations: [{ version: 1, up: () => {} }],
  commands: {
    save: {
      roles: editors,
      kind: 'template',
      execute: (ctx, input) => {
        preview(input, ctx);
        return saveTemplate(ctx, input);
      },
    },
    manage: { roles: editors, kind: 'template', execute: templateCommand },
  },
};
export const analysisQualityDomain: DomainModule = {
  namespace: 'analysis-quality',
  version: 1,
  resource: 'evaluation',
  pages: [
    {
      id: 'A04',
      path: '/analysis/evaluation',
      resource: 'evaluation',
      parentPath: '/analysis/job',
      returnTo: '/analysis/evaluation',
    },
  ],
  migrations: [{ version: 1, up: () => {} }],
  commands: {
    sample: { roles: evaluators, kind: 'evaluation', execute: saveSample },
    evaluate: { roles: evaluators, kind: 'evaluation', execute: evaluate },
  },
};
export function authorizedReport(ctx: DomainReadContext, row: Entity) {
  const report = reportOf(row);
  for (const s of report.samples) {
    const sample = entity(ctx, s.uuid, 'evaluation');
    resolveEvidence(ctx, sampleOf(sample).evidence);
  }
  return report;
}
